import { useState, useEffect } from 'react';
import {
  getReportTypes, createReportType, updateReportType, deleteReportType,
  getReports, createReport, updateReport, deleteReport,
  getTrackerSettings, updateTrackerSettings, testTrackerMail,
  getVessels,
} from './api';

const PRESET_COLORS = [
  '#3b82f6', '#22c55e', '#f59e0b', '#e8001d',
  '#a855f7', '#14b8a6', '#f97316', '#06b6d4',
];

function addMonths(dateStr, months) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

function daysSince(dateStr) {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr + 'T00:00:00');
  return Math.round((today - d) / 86400000);
}

function StatusBadge({ days }) {
  if (days === null || days === undefined) return <span style={s.statusNone}>—</span>;
  if (days > 30) return <span style={s.statusGreen}>ON TRACK</span>;
  if (days > 7) return <span style={s.statusYellow}>DUE SOON</span>;
  if (days >= 0) return <span style={s.statusRed}>URGENT</span>;
  return <span style={s.statusRed}>OVERDUE</span>;
}

function MsoStatus({ msoReviewDate, completionDate, msoFirstDays }) {
  if (msoReviewDate) return <span style={s.statusGreen}>REVIEWED</span>;
  if (!completionDate) return <span style={s.statusNone}>—</span>;
  const ds = daysSince(completionDate);
  if (ds === null) return <span style={s.statusNone}>—</span>;
  if (ds < (msoFirstDays || 14)) return <span style={s.statusYellow}>PENDING</span>;
  return <span style={s.statusRed}>OVERDUE</span>;
}

function daysColor(days) {
  if (days === null || days === undefined) return 'var(--grey)';
  if (days > 30) return '#22c55e';
  if (days > 7) return '#f59e0b';
  return '#ff6b7a';
}

export default function ReportTracker({ setPage, onLogout, isAdmin }) {
  const [reportTypes, setReportTypes] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [reports, setReports] = useState({});
  const [vessels, setVessels] = useState([]);
  const [settings, setSettings] = useState({ notification_email: '', gmail_user: '', gmail_app_password: '' });
  const [loading, setLoading] = useState(true);

  // Report entry modal
  const [showModal, setShowModal] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [mVesselId, setMVesselId] = useState('');
  const [mTypeId, setMTypeId] = useState('');
  const [mCompletionDate, setMCompletionDate] = useState('');
  const [mMsoReviewDate, setMMsoReviewDate] = useState('');
  const [mNotes, setMNotes] = useState('');
  const [modalSaving, setModalSaving] = useState(false);

  // Type modal
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [tmName, setTmName] = useState('');
  const [tmDesc, setTmDesc] = useState('');
  const [tmColor, setTmColor] = useState('#3b82f6');
  const [tmInterval, setTmInterval] = useState(12);
  const [tmFirst, setTmFirst] = useState(30);
  const [tmSecond, setTmSecond] = useState(7);
  const [tmMsoFirst, setTmMsoFirst] = useState(14);
  const [tmMsoSecond, setTmMsoSecond] = useState(30);
  const [tmActive, setTmActive] = useState(true);
  const [typeSaving, setTypeSaving] = useState(false);

  // Settings
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [mailSending, setMailSending] = useState(false);
  const [mailResult, setMailResult] = useState(null);

  useEffect(() => {
    Promise.all([getReportTypes(), getVessels(), getTrackerSettings()])
      .then(([typesRes, vesselsRes, settingsRes]) => {
        const types = typesRes.data || [];
        setReportTypes(types);
        setVessels(vesselsRes.data || []);
        const raw = settingsRes.data || {};
        setSettings({ notification_email: raw.notification_email || '', gmail_user: raw.gmail_user || '', gmail_app_password: '' });
        if (types.length > 0) setActiveTab(types[0].id);
        else setActiveTab('settings');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (typeof activeTab === 'number' && !reports[activeTab]) {
      fetchReports(activeTab);
    }
  }, [activeTab]); // eslint-disable-line

  const fetchReports = async (typeId) => {
    try {
      const r = await getReports(typeId);
      setReports(prev => ({ ...prev, [typeId]: r.data || [] }));
    } catch {}
  };

  const refreshReports = (typeId) => {
    getReports(typeId)
      .then(r => setReports(prev => ({ ...prev, [typeId]: r.data || [] })))
      .catch(() => {});
  };

  // ── Report Entry Modal ────────────────────────────────────────────────────

  const openAddModal = () => {
    setEditingReport(null);
    setMVesselId('');
    setMTypeId(typeof activeTab === 'number' ? String(activeTab) : (reportTypes[0] ? String(reportTypes[0].id) : ''));
    setMCompletionDate('');
    setMMsoReviewDate('');
    setMNotes('');
    setShowModal(true);
  };

  const openEditModal = (report) => {
    setEditingReport(report);
    setMVesselId(String(report.vessel_id));
    setMTypeId(String(report.report_type_id));
    setMCompletionDate(report.completion_date || '');
    setMMsoReviewDate(report.mso_review_date || '');
    setMNotes(report.notes || '');
    setShowModal(true);
  };

  const handleSaveReport = async () => {
    if (!mVesselId || !mTypeId) return;
    setModalSaving(true);
    try {
      const payload = {
        vessel_id: parseInt(mVesselId),
        report_type_id: parseInt(mTypeId),
        completion_date: mCompletionDate || null,
        mso_review_date: mMsoReviewDate || null,
        notes: mNotes,
      };
      if (editingReport) {
        await updateReport(editingReport.id, payload);
      } else {
        await createReport(payload);
      }
      setShowModal(false);
      refreshReports(parseInt(mTypeId));
      if (typeof activeTab === 'number' && parseInt(mTypeId) !== activeTab) {
        refreshReports(activeTab);
      }
    } catch (e) {
      alert('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setModalSaving(false);
    }
  };

  const handleDeleteReport = async (id, typeId) => {
    if (!window.confirm('Delete this report entry?')) return;
    try {
      await deleteReport(id);
      refreshReports(typeId || activeTab);
    } catch (e) {
      alert('Delete failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  // ── Type Modal ────────────────────────────────────────────────────────────

  const openAddTypeModal = () => {
    setEditingType(null);
    setTmName(''); setTmDesc(''); setTmColor('#3b82f6');
    setTmInterval(12); setTmFirst(30); setTmSecond(7);
    setTmMsoFirst(14); setTmMsoSecond(30); setTmActive(true);
    setShowTypeModal(true);
  };

  const openEditTypeModal = (t) => {
    setEditingType(t);
    setTmName(t.name); setTmDesc(t.description || ''); setTmColor(t.color || '#3b82f6');
    setTmInterval(t.report_interval_months); setTmFirst(t.first_notification_days);
    setTmSecond(t.second_notification_days);
    setTmMsoFirst(t.mso_first_notification_days ?? 14);
    setTmMsoSecond(t.mso_second_notification_days ?? 30);
    setTmActive(t.is_active);
    setShowTypeModal(true);
  };

  const handleSaveType = async () => {
    if (!tmName.trim()) return;
    setTypeSaving(true);
    try {
      const payload = {
        name: tmName.trim(), description: tmDesc, color: tmColor,
        first_notification_days: parseInt(tmFirst),
        second_notification_days: parseInt(tmSecond),
        report_interval_months: parseInt(tmInterval),
        is_active: tmActive,
        mso_first_notification_days: parseInt(tmMsoFirst),
        mso_second_notification_days: parseInt(tmMsoSecond),
      };
      if (editingType) {
        await updateReportType(editingType.id, payload);
      } else {
        await createReportType(payload);
      }
      const r = await getReportTypes();
      setReportTypes(r.data || []);
      setShowTypeModal(false);
    } catch (e) {
      alert('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setTypeSaving(false);
    }
  };

  const handleDeleteType = async (id) => {
    if (!window.confirm('Delete this report type? All vessel reports of this type will also be deleted.')) return;
    try {
      await deleteReportType(id);
      const r = await getReportTypes();
      const types = r.data || [];
      setReportTypes(types);
      if (activeTab === id) setActiveTab(types.length > 0 ? types[0].id : 'settings');
    } catch (e) {
      alert('Delete failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  // ── Settings ──────────────────────────────────────────────────────────────

  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    try {
      await updateTrackerSettings(settings);
      alert('Settings saved.');
    } catch (e) {
      alert('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleTestMail = async () => {
    setMailSending(true);
    setMailResult(null);
    try {
      await testTrackerMail();
      setMailResult({ success: true, message: 'Test email sent successfully!' });
    } catch (e) {
      setMailResult({ success: false, message: e.response?.data?.detail || e.message });
    } finally {
      setMailSending(false);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────

  const currentType = reportTypes.find(t => t.id === activeTab) || null;
  const currentReports = typeof activeTab === 'number' ? (reports[activeTab] || []) : [];
  const modalType = reportTypes.find(t => t.id === parseInt(mTypeId)) || null;
  const previewDeadline = mCompletionDate && modalType
    ? addMonths(mCompletionDate, modalType.report_interval_months)
    : '';

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={s.wrapper}>
        <div style={s.topAccent} />
        <div style={s.loadingState}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={s.wrapper}>
      <div style={s.topAccent} />

      <header style={s.header}>
        <div style={s.headerLeft}>
          <button style={s.backBtn} onClick={() => setPage('dashboard')}>← Back</button>
          <div style={s.headerIcon}>📋</div>
          <div>
            <div style={s.headerTitle}>LPSQ REPORT TRACKER</div>
            <div style={s.headerSub}>Compliance report deadlines & automated reminders</div>
          </div>
        </div>
        <button style={s.logoutBtn} onClick={onLogout}>LOGOUT</button>
      </header>

      {/* Tab bar */}
      <div style={s.tabBar}>
        {reportTypes.map(t => (
          <button
            key={t.id}
            style={{
              ...s.tab,
              ...(activeTab === t.id ? { ...s.tabActive, borderBottomColor: t.color, color: 'var(--white)' } : {}),
            }}
            onClick={() => setActiveTab(t.id)}
          >
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: t.color, display: 'inline-block', marginRight: 6, flexShrink: 0 }} />
            {t.name}
          </button>
        ))}
        <button
          style={{ ...s.tab, ...(activeTab === 'settings' ? s.tabActive : {}) }}
          onClick={() => setActiveTab('settings')}
        >
          ⚙ Settings
        </button>
        <button
          style={{ ...s.tab, ...(activeTab === 'report-types' ? s.tabActive : {}) }}
          onClick={() => setActiveTab('report-types')}
        >
          + Report Types
        </button>
      </div>

      <main style={s.main}>

        {/* ── Report type panel ─────────────────────────────────────────── */}
        {typeof activeTab === 'number' && currentType && (
          <div>
            {/* Stats bar */}
            {(() => {
              const overdue   = currentReports.filter(r => r.days_remaining !== null && r.days_remaining <= 7).length;
              const dueSoon   = currentReports.filter(r => r.days_remaining !== null && r.days_remaining > 7 && r.days_remaining <= 30).length;
              const onTrack   = currentReports.filter(r => r.days_remaining !== null && r.days_remaining > 30).length;
              const msoPend   = currentReports.filter(r => !r.mso_review_date).length;
              return (
                <div style={s.statsBar}>
                  <div style={s.statCard}>
                    <div style={s.statVal}>{currentReports.length}</div>
                    <div style={s.statLbl}>TOTAL</div>
                  </div>
                  <div style={{ ...s.statCard, borderTop: '2px solid #22c55e' }}>
                    <div style={{ ...s.statVal, color: '#22c55e' }}>{onTrack}</div>
                    <div style={s.statLbl}>ON TRACK</div>
                  </div>
                  <div style={{ ...s.statCard, borderTop: '2px solid #f59e0b' }}>
                    <div style={{ ...s.statVal, color: '#f59e0b' }}>{dueSoon}</div>
                    <div style={s.statLbl}>DUE SOON</div>
                  </div>
                  <div style={{ ...s.statCard, borderTop: '2px solid #e8001d' }}>
                    <div style={{ ...s.statVal, color: '#ff6b7a' }}>{overdue}</div>
                    <div style={s.statLbl}>URGENT / OVERDUE</div>
                  </div>
                  <div style={{ ...s.statCard, borderTop: '2px solid #f59e0b' }}>
                    <div style={{ ...s.statVal, color: '#f59e0b' }}>{msoPend}</div>
                    <div style={s.statLbl}>MSO PENDING</div>
                  </div>
                </div>
              );
            })()}

            {/* Toolbar */}
            <div style={s.toolbar}>
              <div style={{ fontSize: '12px', color: 'var(--grey)', fontFamily: 'var(--font-mono)' }}>
                Interval:&nbsp;<strong style={{ color: 'var(--white)' }}>{currentType.report_interval_months}mo</strong>
                &nbsp;·&nbsp;
                Deadline notif.:&nbsp;<strong style={{ color: 'var(--white)' }}>{currentType.first_notification_days}d</strong> / <strong style={{ color: 'var(--white)' }}>{currentType.second_notification_days}d</strong>
                &nbsp;·&nbsp;
                MSO notif.:&nbsp;<strong style={{ color: '#06b6d4' }}>{currentType.mso_first_notification_days ?? 14}d</strong> / <strong style={{ color: '#06b6d4' }}>{currentType.mso_second_notification_days ?? 30}d</strong>
              </div>
              <button style={s.addBtn} onClick={openAddModal}>+ ADD ENTRY</button>
            </div>

            {/* Table */}
            <div style={s.tableWrap}>
              <div style={s.tableHeader}>
                <span>VESSEL NAME</span>
                <span>COMPLETION</span>
                <span>MSO REVIEW</span>
                <span>DEADLINE</span>
                <span>DAYS REM.</span>
                <span>STATUS</span>
                <span>MSO STATUS</span>
                <span>ACTIONS</span>
              </div>
              {currentReports.length === 0 ? (
                <div style={s.emptyState}>
                  <div style={{ fontSize: '32px', marginBottom: 12 }}>📋</div>
                  No entries yet. Click <strong>+ ADD ENTRY</strong> to log the first vessel report.
                </div>
              ) : currentReports.map(r => (
                <div key={r.id} style={s.tableRow}>
                  <span>
                    <div style={s.vesselName}>{r.ship_name}</div>
                    <div style={s.mono}>{r.imo_number}</div>
                  </span>
                  <span style={s.mono}>{r.completion_date || '—'}</span>
                  <span style={s.mono}>{r.mso_review_date || '—'}</span>
                  <span style={s.mono}>{r.deadline_date || '—'}</span>
                  <span style={{ ...s.mono, color: daysColor(r.days_remaining), fontWeight: '700' }}>
                    {r.days_remaining === null ? '—' : r.days_remaining < 0 ? `${Math.abs(r.days_remaining)}d ago` : `${r.days_remaining}d`}
                  </span>
                  <span><StatusBadge days={r.days_remaining} /></span>
                  <span>
                    <MsoStatus
                      msoReviewDate={r.mso_review_date}
                      completionDate={r.completion_date}
                      msoFirstDays={r.mso_first_notification_days ?? currentType.mso_first_notification_days ?? 14}
                    />
                  </span>
                  <span style={s.actions}>
                    <button style={s.editBtn} onClick={() => openEditModal(r)}>Edit</button>
                    <button style={s.deleteBtn} onClick={() => handleDeleteReport(r.id, activeTab)}>Del</button>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Settings panel ────────────────────────────────────────────── */}
        {activeTab === 'settings' && (
          <div style={s.settingsPanel}>
            <div style={s.panelTitle}>Notification Settings</div>
            <div style={s.panelDesc}>Configure Gmail SMTP credentials for automated deadline and MSO review reminders. The system checks daily at 08:00 UTC.</div>

            <div style={s.formGroup}>
              <label style={s.label}>Notification Email (recipient)</label>
              <input
                style={s.input}
                type="email"
                value={settings.notification_email}
                onChange={e => setSettings(p => ({ ...p, notification_email: e.target.value }))}
                placeholder="reports@yourcompany.com"
              />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Gmail Sender Address</label>
              <input
                style={s.input}
                type="email"
                value={settings.gmail_user}
                onChange={e => setSettings(p => ({ ...p, gmail_user: e.target.value }))}
                placeholder="sender@gmail.com"
              />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Gmail App Password</label>
              <input
                style={s.input}
                type="password"
                value={settings.gmail_app_password}
                onChange={e => setSettings(p => ({ ...p, gmail_app_password: e.target.value }))}
                placeholder="Leave blank to keep existing password"
              />
              <div style={s.hint}>
                Use a Gmail App Password, not your Google account password.
                Generate at <em>myaccount.google.com → Security → App passwords</em>.
              </div>
            </div>

            <div style={s.settingsBtns}>
              <button style={s.saveBtn} onClick={handleSaveSettings} disabled={settingsSaving}>
                {settingsSaving ? 'Saving...' : 'SAVE SETTINGS'}
              </button>
              <button style={s.testBtn} onClick={handleTestMail} disabled={mailSending}>
                {mailSending ? 'Sending...' : 'SEND TEST MAIL'}
              </button>
            </div>

            {mailResult && (
              <div style={{
                ...s.mailResult,
                background: mailResult.success ? 'rgba(34,197,94,0.08)' : 'rgba(232,0,29,0.08)',
                borderColor: mailResult.success ? 'rgba(34,197,94,0.3)' : 'rgba(232,0,29,0.3)',
                color: mailResult.success ? '#22c55e' : '#ff6b7a',
              }}>
                {mailResult.success ? '✓ ' : '✗ '}{mailResult.message}
              </div>
            )}
          </div>
        )}

        {/* ── Report Types admin panel ──────────────────────────────────── */}
        {activeTab === 'report-types' && (
          <div>
            <div style={s.toolbar}>
              <div style={s.panelTitle}>Report Types</div>
              <button style={s.addBtn} onClick={openAddTypeModal}>+ NEW TYPE</button>
            </div>
            <div style={s.tableWrap}>
              <div style={{ ...s.tableHeader, gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1fr' }}>
                <span>NAME</span><span>INTERVAL</span><span>1ST NOTIF.</span><span>2ND NOTIF.</span><span>MSO 1ST</span><span>MSO 2ND</span><span>ACTIONS</span>
              </div>
              {reportTypes.length === 0 ? (
                <div style={s.emptyState}>No report types yet. Click + NEW TYPE to create the first one.</div>
              ) : reportTypes.map(t => (
                <div key={t.id} style={{ ...s.tableRow, gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1fr' }}>
                  <span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: t.color, flexShrink: 0, display: 'inline-block' }} />
                      <strong>{t.name}</strong>
                      {!t.is_active && <span style={{ fontSize: '10px', color: 'var(--grey)', marginLeft: 4 }}>(inactive)</span>}
                    </div>
                    {t.description && <div style={{ fontSize: '11px', color: 'var(--grey)', marginTop: 2, paddingLeft: 18 }}>{t.description}</div>}
                  </span>
                  <span style={s.mono}>{t.report_interval_months}mo</span>
                  <span style={s.mono}>{t.first_notification_days}d</span>
                  <span style={s.mono}>{t.second_notification_days}d</span>
                  <span style={{ ...s.mono, color: '#06b6d4' }}>{t.mso_first_notification_days ?? 14}d</span>
                  <span style={{ ...s.mono, color: '#06b6d4' }}>{t.mso_second_notification_days ?? 30}d</span>
                  <span style={s.actions}>
                    <button style={s.editBtn} onClick={() => openEditTypeModal(t)}>Edit</button>
                    <button style={s.deleteBtn} onClick={() => handleDeleteType(t.id)}>Del</button>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {reportTypes.length === 0 && activeTab !== 'settings' && activeTab !== 'report-types' && (
          <div style={s.emptyState}>
            <div style={{ fontSize: '40px', marginBottom: 16 }}>📋</div>
            <div style={{ fontSize: '15px', fontWeight: '700', marginBottom: 8 }}>No report types yet</div>
            <div style={{ fontSize: '13px', color: 'var(--grey)', marginBottom: 20 }}>
              Go to the + Report Types tab to create your first report type.
            </div>
          </div>
        )}
      </main>

      {/* ── Add/Edit Report Entry Modal ─────────────────────────────────────── */}
      {showModal && (
        <div style={s.overlay} onClick={() => setShowModal(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <div style={s.modalTitle}>{editingReport ? 'EDIT REPORT ENTRY' : 'ADD REPORT ENTRY'}</div>
              <button style={s.modalClose} onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formGroup}>
                <label style={s.label}>Report Type *</label>
                <select style={s.input} value={mTypeId} onChange={e => setMTypeId(e.target.value)}>
                  <option value="">— Select type —</option>
                  {reportTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Vessel *</label>
                <select style={s.input} value={mVesselId} onChange={e => setMVesselId(e.target.value)}>
                  <option value="">— Select vessel —</option>
                  {vessels.map(v => <option key={v.id} value={v.id}>{v.ship_name} ({v.imo_number})</option>)}
                </select>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Completion Date</label>
                  <input style={s.input} type="date" value={mCompletionDate} onChange={e => setMCompletionDate(e.target.value)} />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>MSO Review Date</label>
                  <input style={s.input} type="date" value={mMsoReviewDate} onChange={e => setMMsoReviewDate(e.target.value)} />
                </div>
              </div>
              {previewDeadline && (
                <div style={s.deadlinePreview}>
                  Auto-deadline: <strong>{previewDeadline}</strong>
                  <span style={{ opacity: 0.7, marginLeft: 8 }}>({modalType?.report_interval_months} months from completion date)</span>
                </div>
              )}
              <div style={s.formGroup}>
                <label style={s.label}>Notes</label>
                <textarea
                  style={{ ...s.input, minHeight: 72, resize: 'vertical' }}
                  value={mNotes}
                  onChange={e => setMNotes(e.target.value)}
                  placeholder="Optional notes..."
                />
              </div>
            </div>
            <div style={s.modalFooter}>
              <button style={s.cancelBtn} onClick={() => setShowModal(false)}>Cancel</button>
              <button
                style={{ ...s.saveBtn, opacity: (!mVesselId || !mTypeId) ? 0.5 : 1 }}
                onClick={handleSaveReport}
                disabled={modalSaving || !mVesselId || !mTypeId}
              >
                {modalSaving ? 'Saving...' : 'SAVE'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add/Edit Report Type Modal ──────────────────────────────────────── */}
      {showTypeModal && (
        <div style={s.overlay} onClick={() => setShowTypeModal(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <div style={s.modalTitle}>{editingType ? 'EDIT REPORT TYPE' : 'NEW REPORT TYPE'}</div>
              <button style={s.modalClose} onClick={() => setShowTypeModal(false)}>✕</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formGroup}>
                <label style={s.label}>Name *</label>
                <input style={s.input} value={tmName} onChange={e => setTmName(e.target.value)} placeholder="e.g. Annual Safety Inspection" />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Description</label>
                <input style={s.input} value={tmDesc} onChange={e => setTmDesc(e.target.value)} placeholder="Optional description" />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Color</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                  {PRESET_COLORS.map(c => (
                    <button
                      key={c}
                      onClick={() => setTmColor(c)}
                      style={{
                        width: 28, height: 28, borderRadius: '50%', background: c, cursor: 'pointer',
                        border: tmColor === c ? '3px solid white' : '2px solid transparent',
                        outline: tmColor === c ? `2px solid ${c}` : 'none',
                      }}
                    />
                  ))}
                </div>
              </div>
              <div style={{ ...s.formGroup, marginTop: 4 }}>
                <div style={s.sectionDivider}>DEADLINE NOTIFICATIONS</div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Interval (months)</label>
                  <input style={s.input} type="number" min="1" max="120" value={tmInterval} onChange={e => setTmInterval(e.target.value)} />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>1st Reminder (days)</label>
                  <input style={s.input} type="number" min="1" max="365" value={tmFirst} onChange={e => setTmFirst(e.target.value)} />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>2nd Reminder (days)</label>
                  <input style={s.input} type="number" min="1" max="365" value={tmSecond} onChange={e => setTmSecond(e.target.value)} />
                </div>
              </div>
              <div style={{ ...s.formGroup, marginTop: 4 }}>
                <div style={s.sectionDivider}>MSO REVIEW NOTIFICATIONS</div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>MSO Review 1st Reminder (days)</label>
                  <input style={s.input} type="number" min="1" max="365" value={tmMsoFirst} onChange={e => setTmMsoFirst(e.target.value)} />
                  <div style={s.hint}>Days after completion date before first MSO reminder</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>MSO Review 2nd Reminder (days)</label>
                  <input style={s.input} type="number" min="1" max="365" value={tmMsoSecond} onChange={e => setTmMsoSecond(e.target.value)} />
                  <div style={s.hint}>Days after completion date before second MSO reminder</div>
                </div>
              </div>
              <div style={s.formGroup}>
                <label style={{ ...s.label, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={tmActive} onChange={e => setTmActive(e.target.checked)} />
                  Active (visible as a tab)
                </label>
              </div>
            </div>
            <div style={s.modalFooter}>
              <button style={s.cancelBtn} onClick={() => setShowTypeModal(false)}>Cancel</button>
              <button
                style={{ ...s.saveBtn, opacity: !tmName.trim() ? 0.5 : 1 }}
                onClick={handleSaveType}
                disabled={typeSaving || !tmName.trim()}
              >
                {typeSaving ? 'Saving...' : 'SAVE'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  wrapper: { minHeight: '100vh', background: 'var(--navy)', display: 'flex', flexDirection: 'column' },
  topAccent: { height: '3px', background: 'linear-gradient(90deg, #06b6d4 0%, #3b82f6 60%, #06b6d4 100%)' },
  loadingState: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--grey)', fontFamily: 'var(--font-mono)', fontSize: '14px' },

  header: {
    background: 'var(--navy-mid)', borderBottom: '1px solid var(--navy-border)',
    padding: '0 32px', height: '60px', display: 'flex',
    alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '14px' },
  headerIcon: { fontSize: '22px' },
  headerTitle: { fontSize: '13px', fontWeight: '700', letterSpacing: '2px', color: '#06b6d4', fontFamily: 'var(--font-mono)' },
  headerSub: { fontSize: '11px', color: 'var(--grey)', marginTop: '2px' },
  backBtn: {
    background: 'transparent', border: '1px solid var(--navy-light)',
    color: 'var(--grey)', padding: '6px 14px', borderRadius: '6px',
    fontSize: '11px', cursor: 'pointer', fontFamily: 'var(--font-mono)',
  },
  logoutBtn: {
    background: 'transparent', border: '1px solid var(--navy-light)',
    color: 'var(--grey)', padding: '6px 16px', borderRadius: '6px',
    fontSize: '11px', letterSpacing: '1px', cursor: 'pointer', fontFamily: 'var(--font-mono)',
  },

  tabBar: {
    background: 'var(--navy-mid)', borderBottom: '1px solid var(--navy-border)',
    padding: '0 28px', display: 'flex', gap: '2px', overflowX: 'auto', flexShrink: 0,
  },
  tab: {
    background: 'transparent', border: 'none', borderBottom: '2px solid transparent',
    color: 'var(--grey)', padding: '12px 16px', fontSize: '12px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap',
    letterSpacing: '0.5px', display: 'flex', alignItems: 'center',
  },
  tabActive: { color: 'var(--white)', borderBottomColor: '#06b6d4' },

  main: { flex: 1, padding: '24px 32px', overflowY: 'auto' },

  statsBar: {
    display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)',
    gap: '12px', marginBottom: '20px',
  },
  statCard: {
    background: 'var(--navy-mid)', border: '1px solid var(--navy-border)',
    borderRadius: '10px', padding: '14px 18px', borderTop: '2px solid var(--navy-border)',
  },
  statVal: { fontSize: '24px', fontWeight: '800', color: '#c9a84c', fontFamily: 'var(--font-mono)', lineHeight: 1 },
  statLbl: { fontSize: '9px', color: 'var(--grey)', letterSpacing: '1.5px', fontFamily: 'var(--font-mono)', marginTop: '6px' },

  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' },
  addBtn: {
    background: '#06b6d4', border: 'none', borderRadius: '7px',
    padding: '8px 18px', color: 'white', fontSize: '11px',
    fontWeight: '700', letterSpacing: '1px', cursor: 'pointer', fontFamily: 'var(--font-mono)',
  },

  tableWrap: {
    background: 'var(--navy-mid)', borderRadius: '10px',
    overflow: 'hidden', border: '1px solid var(--navy-border)',
  },
  tableHeader: {
    display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr',
    padding: '12px 20px', background: 'rgba(255,255,255,0.03)',
    fontSize: '10px', fontWeight: '700', letterSpacing: '1.5px',
    color: 'var(--grey)', fontFamily: 'var(--font-mono)',
    borderBottom: '1px solid var(--navy-border)',
  },
  tableRow: {
    display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr',
    padding: '14px 20px', borderBottom: '1px solid var(--navy-border)',
    fontSize: '12px', alignItems: 'center',
  },
  vesselName: { fontWeight: '600', color: 'var(--white)', lineHeight: '1.4' },
  mono: { fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--grey)' },
  actions: { display: 'flex', gap: '6px' },
  editBtn: {
    background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.3)',
    color: '#c9a84c', padding: '4px 10px', borderRadius: '4px',
    fontSize: '11px', cursor: 'pointer', fontWeight: '600',
  },
  deleteBtn: {
    background: 'rgba(232,0,29,0.08)', border: '1px solid rgba(232,0,29,0.25)',
    color: '#ff6b7a', padding: '4px 10px', borderRadius: '4px',
    fontSize: '11px', cursor: 'pointer', fontWeight: '600',
  },
  emptyState: {
    padding: '48px 24px', textAlign: 'center',
    color: 'var(--grey)', fontSize: '13px', lineHeight: '1.6',
  },

  statusGreen: {
    padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: '700',
    background: 'rgba(34,197,94,0.12)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)',
    fontFamily: 'var(--font-mono)',
  },
  statusYellow: {
    padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: '700',
    background: 'rgba(245,158,11,0.12)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)',
    fontFamily: 'var(--font-mono)',
  },
  statusRed: {
    padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: '700',
    background: 'rgba(232,0,29,0.12)', color: '#ff6b7a', border: '1px solid rgba(232,0,29,0.3)',
    fontFamily: 'var(--font-mono)',
  },
  statusNone: { color: 'var(--grey)', fontFamily: 'var(--font-mono)', fontSize: '12px' },

  settingsPanel: { maxWidth: '560px' },
  panelTitle: { fontSize: '15px', fontWeight: '700', marginBottom: '6px' },
  panelDesc: { fontSize: '12px', color: 'var(--grey)', marginBottom: '24px', lineHeight: '1.6' },

  formGroup: { marginBottom: '16px' },
  formRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' },
  label: {
    display: 'block', fontSize: '11px', fontWeight: '600',
    letterSpacing: '1px', color: 'var(--grey)', fontFamily: 'var(--font-mono)', marginBottom: '6px',
  },
  input: {
    width: '100%', background: 'var(--navy)', border: '1px solid var(--navy-light)',
    borderRadius: '7px', padding: '9px 12px', color: 'var(--white)',
    fontSize: '13px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
  },
  hint: { fontSize: '11px', color: 'var(--grey)', marginTop: '5px', lineHeight: '1.5' },
  sectionDivider: {
    fontSize: '9px', fontWeight: '700', letterSpacing: '2px', color: 'var(--grey)',
    fontFamily: 'var(--font-mono)', borderBottom: '1px solid var(--navy-border)',
    paddingBottom: '6px', marginBottom: '4px', opacity: 0.7,
  },

  settingsBtns: { display: 'flex', gap: '10px', marginTop: '8px' },
  saveBtn: {
    background: '#06b6d4', border: 'none', borderRadius: '7px',
    padding: '9px 22px', color: 'white', fontSize: '11px',
    fontWeight: '700', letterSpacing: '1px', cursor: 'pointer', fontFamily: 'var(--font-mono)',
  },
  testBtn: {
    background: 'transparent', border: '1px solid rgba(6,182,212,0.4)',
    borderRadius: '7px', padding: '9px 22px', color: '#06b6d4',
    fontSize: '11px', fontWeight: '700', letterSpacing: '1px',
    cursor: 'pointer', fontFamily: 'var(--font-mono)',
  },
  cancelBtn: {
    background: 'transparent', border: '1px solid var(--navy-light)',
    color: 'var(--grey)', padding: '9px 22px', borderRadius: '7px',
    fontSize: '12px', cursor: 'pointer', fontFamily: 'var(--font-mono)',
  },
  mailResult: {
    marginTop: '14px', padding: '10px 16px', borderRadius: '7px',
    border: '1px solid', fontSize: '12px', fontFamily: 'var(--font-mono)',
  },

  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
  },
  modal: {
    background: 'var(--navy-mid)', border: '1px solid var(--navy-border)',
    borderRadius: '14px', width: '560px', maxWidth: '95vw', maxHeight: '90vh',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
  },
  modalHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '20px 24px', borderBottom: '1px solid var(--navy-border)',
  },
  modalTitle: {
    fontSize: '12px', fontWeight: '700', letterSpacing: '2px',
    fontFamily: 'var(--font-mono)', color: '#06b6d4',
  },
  modalClose: {
    background: 'transparent', border: 'none', color: 'var(--grey)',
    fontSize: '16px', cursor: 'pointer', padding: '4px 8px',
  },
  modalBody: { padding: '20px 24px', overflowY: 'auto', flex: 1 },
  modalFooter: {
    padding: '16px 24px', borderTop: '1px solid var(--navy-border)',
    display: 'flex', justifyContent: 'flex-end', gap: '10px',
  },
  deadlinePreview: {
    background: 'rgba(6,182,212,0.08)', border: '1px solid rgba(6,182,212,0.2)',
    borderRadius: '6px', padding: '8px 12px', fontSize: '12px',
    color: '#06b6d4', marginBottom: '12px', fontFamily: 'var(--font-mono)',
  },
};
